import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import Link from 'next/link';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { down, up } from 'styled-breakpoints';
import styled from 'styled-components';

import {
  Retailer,
  UserJourneyEnum,
} from '@rebound/shared-models/retailer/retailer.model';

import { colorSchemeConfig } from '../theme/colorScheme';
import { SecondaryButton } from './Button';
import HelpSelect from './HelpSelect';
import LanguageSelect from './LanguageSelect';

type LandingPageHeaderProps = {
  logoSrc: string;
  localesOptions: Record<string, string>;
  helpOptions: any;
  isAuthenticatedUser: boolean;
  colorScheme: string;
  retailerData: Retailer | undefined;
};

const LandingPageHeader = ({
  logoSrc,
  localesOptions,
  helpOptions,
  isAuthenticatedUser,
  colorScheme,
  retailerData,
}: LandingPageHeaderProps) => {
  const { t } = useTranslation();
  const [drawerOpened, setDrawerOpened] = useState(false);
  const helpOptionsSelect = {
    [helpOptions.returnPolicy]: retailerData?.returnPolicy,
    [helpOptions.faultyItem]: retailerData?.faultyItem,
    [helpOptions.support]: retailerData?.support,
  };
  const hasSelectedHelpOptions =
    helpOptionsSelect[helpOptions.returnPolicy] ||
    helpOptionsSelect[helpOptions.faultyItem] ||
    helpOptionsSelect[helpOptions.support]
      ? true
      : false;

  const logoLinkRedirectPage =
    retailerData?.userJourneyRadio === UserJourneyEnum.EMAIL_VERIFICATION
      ? isAuthenticatedUser
        ? '/dashboard/orders'
        : '/'
      : retailerData?.userJourneyRadio ===
          UserJourneyEnum.RETURN_WITHOUT_SALES_ORDER
        ? '/create-return'
        : '/search-order';

  return (
    <>
      <HeaderWrapper drawerIsOpened={drawerOpened}>
        <HamburgerMenu>
          <DrawerButton
            scheme={colorScheme}
            isOpened={drawerOpened}
            onClick={() => setDrawerOpened((prev) => !prev)}
          >
            {drawerOpened ? <DrawerXIcon /> : <DrawerMenuIcon />}
          </DrawerButton>
        </HamburgerMenu>
        <LogoWrapper>
          <LogoLink href={logoLinkRedirectPage} passHref legacyBehavior>
            <a>
              <img src={logoSrc} alt="logo" />
            </a>
          </LogoLink>
        </LogoWrapper>
        <DesktopButtonsWrapper>
          {hasSelectedHelpOptions ? (
            <HelpSelect localesOptions={helpOptionsSelect} />
          ) : null}
          <LanguageSelect localesOptions={localesOptions} />
          {isAuthenticatedUser &&
          retailerData?.userJourneyRadio ===
            UserJourneyEnum.EMAIL_VERIFICATION ? (
            <Link href="/logout" passHref>
              <LogoutLink as="div" scheme={colorScheme}>
                <ArrowRightOnRectangleIcon />
                <LogoutLinkLabel>
                  {t('landing.logout') as string}
                </LogoutLinkLabel>
              </LogoutLink>
            </Link>
          ) : null}
        </DesktopButtonsWrapper>
      </HeaderWrapper>

      <HamburgerMenu>
        <MobileDrawerWrapper visible={drawerOpened}>
          <MobileDrawerLinks>
            <LanguageSelect localesOptions={localesOptions} />
            {hasSelectedHelpOptions ? (
              <HelpSelect localesOptions={helpOptionsSelect} />
            ) : null}
            {isAuthenticatedUser &&
            retailerData?.userJourneyRadio ===
              UserJourneyEnum.EMAIL_VERIFICATION ? (
              <>
                <Link href="/logout" passHref>
                  <LogoutMobileLink as="div" scheme={colorScheme}>
                    <LogoutLinkLabel>
                      {t('landing.logout') as string}
                    </LogoutLinkLabel>
                  </LogoutMobileLink>
                </Link>
                <BorderBottom></BorderBottom>
              </>
            ) : null}
          </MobileDrawerLinks>
        </MobileDrawerWrapper>
      </HamburgerMenu>
    </>
  );
};

export const BorderBottom = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayLight4};
`;

export const HeaderWrapper = styled.div<{ drawerIsOpened: boolean }>`
  background: ${(props) => props.theme.colors.white};
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 72px;

  ${down('xs')} {
    border-bottom: 1px solid ${({ theme }) => theme.colors.grayLight4};
    position: ${({ drawerIsOpened }) =>
      drawerIsOpened ? 'fixed' : 'relative'};
    z-index: ${({ drawerIsOpened }) => (drawerIsOpened ? 1031 : 'auto')};
    left: ${({ drawerIsOpened }) => (drawerIsOpened ? 0 : 'auto')};
    right: ${({ drawerIsOpened }) => (drawerIsOpened ? 0 : 'auto')};
  }

  ${down('sm')} {
    justify-content: normal;
  }

  ${up('sm')} {
    padding: 0 10px;
    height: 64px;
    box-shadow:
      0 4px 8px rgba(0, 0, 0, 0.04),
      0 0 2px rgba(0, 0, 0, 0.06),
      0 0 1px rgba(0, 0, 0, 0.04);
  }

  ${up('lg')} {
    padding: 0 64px;
  }
`;

export const LogoWrapper = styled.div`
  height: 32px;
  width: auto;

  img {
    max-height: 32px;
    max-width: 100%;
    height: auto;
    width: auto;
    object-fit: contain;
  }
`;

const LogoLink = styled(Link)`
  a {
    cursor: pointer;
  }
`;

const LogoutLink = styled(SecondaryButton)`
  padding: 10px 22px;

  svg {
    height: 20px;
    margin-right: 8px;
  }
`;

const LogoutLinkLabel = styled.span`
  line-height: 20px;
  font-size: 14px;
`;

const LogoutMobileLink = styled.a<{ scheme: string }>`
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  padding: 9px 35px 9px 18px;
  color: ${({ scheme }) =>
    colorSchemeConfig[scheme] ? colorSchemeConfig[scheme].v500 : ''};
  transition: 0.3s ease;

  &:hover {
    color: ${({ scheme }) =>
      colorSchemeConfig[scheme] ? colorSchemeConfig[scheme].v700 : ''};
  }
`;

const DesktopButtonsWrapper = styled.div`
  display: none;

  & > *:not(:last-child) {
    margin-right: 16px;
  }

  ${up('sm')} {
    display: flex;

    & > *:not(:last-child) {
      margin-right: 24px;
    }
  }

  ${up('lg')} {
    & > *:not(:last-child) {
      margin-right: 32px;
    }
  }
`;

const DrawerButton = styled.button<{ scheme: string; isOpened: boolean }>`
  display: none;
  padding: 5px;
  background: transparent;
  border: transparent;
  align-items: center;
  transition: 0.3s ease;
  cursor: pointer;

  ${down('sm')} {
    display: flex;
  }
`;

const DrawerMenuIcon = styled(Bars3Icon)`
  width: 21px;
`;
const DrawerXIcon = styled(XMarkIcon)`
  width: 21px;
  color: ${({ theme }) => theme.colors.grayDark};
`;

const MobileDrawerWrapper = styled.div<{ visible: boolean }>`
  padding: 0 20px;
  margin-bottom: 10px;
  display: ${({ visible }) => (visible ? 'block' : 'none')};
`;

const HamburgerMenu = styled.div`
  background-color: ${({ theme }) => theme.colors.white};

  ${up('sm')} {
    display: none;
  }
`;

const MobileDrawerLinks = styled.div`
  display: flex;
  gap: 5px;
  flex-direction: column;
  padding: 0 20px;
`;

export default LandingPageHeader;
